export interface BlogPost {
  slug: string;
  category: string;
  date: string;
  readTime: string;
  title: string;
  excerpt: string;
  img: string;
  featured: boolean;
  body: {
    type: "paragraph" | "heading" | "quote" | "list";
    content: string | string[];
  }[];
}

export const blogPosts: BlogPost[] = [
  {
    slug: "auria-recepcionista-ia-24-7",
    category: "Producto",
    date: "19 mar 2026",
    readTime: "5 min",
    featured: true,
    title: "Auria: tu recepcionista con IA disponible 24/7/365, sin descansos ni llamadas perdidas",
    excerpt:
      "Descubre cómo Auria gestiona cada llamada entrante de tu negocio con voz natural, responde preguntas frecuentes y agenda citas automáticamente, incluso a las 3 de la madrugada.",
    img: "https://public.readdy.ai/ai/img_res/564138fce05ed50287d30c2b1b30fd62.jpg",
    body: [
      {
        type: "paragraph",
        content: "Imagina que tu negocio nunca vuelve a perder una llamada. Que cuando un paciente marca tu número un domingo a las 22:00, alguien responde. No un contestador automático, no un buzón de voz. Una voz natural, clara, que entiende lo que el cliente dice y le da exactamente lo que necesita: una cita, una respuesta, una confirmación."
      },
      {
        type: "heading",
        content: "Qué es Auria exactamente"
      },
      {
        type: "paragraph",
        content: "Auria es un agente de voz con inteligencia artificial diseñado específicamente para negocios con alta demanda de atención telefónica. A diferencia de los sistemas IVR clásicos —esos menús de \"pulse 1 para... pulse 2 para...\" que todos detestamos— Auria mantiene conversaciones reales. Escucha, comprende el contexto, hace preguntas cuando es necesario, y actúa."
      },
      {
        type: "paragraph",
        content: "Su función principal es la recepción de llamadas: agenda citas en tu Google Calendar o sistema de reservas, responde preguntas frecuentes configuradas por ti, transfiere llamadas urgentes a humanos y genera un resumen de cada interacción en tu panel de control."
      },
      {
        type: "heading",
        content: "Por qué el 67% de los clientes no deja buzón de voz"
      },
      {
        type: "paragraph",
        content: "Múltiples estudios de experiencia de cliente confirman que la mayoría de personas cuelga si no le atienden en los primeros 20 segundos. No dejan mensaje. Llaman al siguiente negocio de la lista. Con Auria, ese problema desaparece: la llamada se atiende al primer tono, siempre, en cualquier idioma, en cualquier franja horaria."
      },
      {
        type: "heading",
        content: "Cómo funciona en la práctica"
      },
      {
        type: "list",
        content: [
          "El cliente llama a tu número de siempre. Auria atiende en menos de 2 segundos.",
          "La IA identifica la intención: reservar cita, cancelar, preguntar precio, hablar con alguien del equipo...",
          "Gestiona la solicitud: comprueba disponibilidad en tiempo real, confirma la reserva y envía un SMS o email al cliente.",
          "Todo queda registrado en tu panel: transcripción de la llamada, datos del cliente y próximo paso."
        ]
      },
      {
        type: "paragraph",
        content: "La configuración inicial tarda menos de 30 minutos. Conectas tu calendario, defines las preguntas frecuentes y el horario de atención, y Auria ya está listo para trabajar. Sin código, sin equipos técnicos, sin mantenimiento."
      },
      {
        type: "heading",
        content: "El impacto real: más reservas, menos carga"
      },
      {
        type: "paragraph",
        content: "Los negocios que usan Auria reportan de media un 40% más de citas gestionadas al mes en los primeros 60 días, y una reducción drástica del tiempo que su equipo dedica a responder el teléfono. Ese tiempo se redirige a lo que realmente importa: atender bien a quien ya está en consulta, en la mesa o en la silla."
      }
    ]
  },
  {
    slug: "clinicas-dentales-auria",
    category: "Casos de uso",
    date: "14 mar 2026",
    readTime: "4 min",
    featured: false,
    title: "Clínicas dentales: cómo Auria eliminó el 100% de las llamadas perdidas en horario de consulta",
    excerpt:
      "Mientras el dentista trabaja, Auria atiende, agenda y confirma citas. Varias clínicas en España ya han triplicado sus reservas en menos de 60 días.",
    img: "https://readdy.ai/api/search-image?query=bright%20modern%20dental%20clinic%20reception%20area%20with%20white%20walls%20and%20warm%20lighting%2C%20clean%20professional%20healthcare%20interior%2C%20empty%20reception%20desk%2C%20welcoming%20and%20minimal%20design%2C%20architectural%20photography&width=800&height=500&seq=auria_blog_02&orientation=landscape",
    body: [
      {
        type: "paragraph",
        content: "En una clínica dental, el teléfono no para. Primeras consultas, revisiones, urgencias, confirmaciones de cita... Y al mismo tiempo, la recepcionista está cobrando a un paciente, explicando un presupuesto o gestionando el seguro de otro. El resultado es inevitable: llamadas que nadie coge, pacientes que llaman a otra clínica, ingresos que se pierden en silencio."
      },
      {
        type: "heading",
        content: "El problema real de las clínicas en horario punta"
      },
      {
        type: "paragraph",
        content: "Las franjas más críticas —de 9 a 11 y de 17 a 19— son precisamente cuando más llamadas entran y cuando el equipo está más ocupado. Las clínicas que han adoptado Auria detectaron que perdían entre 10 y 25 llamadas diarias solo en esas horas. Cada llamada perdida es, de media, un ticket de 80-300€ que se va a la competencia."
      },
      {
        type: "heading",
        content: "Cómo trabaja Auria en una clínica dental"
      },
      {
        type: "list",
        content: [
          "Atiende todas las llamadas entrantes, incluso mientras la recepcionista está ocupada.",
          "Ofrece los servicios disponibles y agenda la primera visita directamente en el calendario del profesional.",
          "Gestiona confirmaciones y recordatorios automáticos 24h antes de la cita.",
          "Detecta urgencias y las deriva al número de guardia configurado.",
          "Proporciona información de precios, seguros aceptados y localización."
        ]
      },
      {
        type: "heading",
        content: "Resultados en 60 días"
      },
      {
        type: "paragraph",
        content: "Las clínicas que han implementado Auria reportan una media de 3x más citas agendadas en el primer mes, cero llamadas perdidas en horario laboral, y un ahorro de entre 2 y 4 horas diarias de gestión telefónica para el equipo de recepción. La recepcionista sigue siendo esencial —para la experiencia presencial— pero ya no está encadenada al teléfono."
      },
      {
        type: "paragraph",
        content: "El proceso de integración con los calendarios habituales de clínica (Google Calendar, Clinicmaster, Dentina, Gesden) está completamente automatizado y no requiere cambiar ningún flujo de trabajo existente."
      }
    ]
  },
  {
    slug: "restaurantes-reservas-auria",
    category: "Casos de uso",
    date: "8 mar 2026",
    readTime: "3 min",
    featured: false,
    title: "Restaurantes: Auria gestiona reservas de mesa mientras tú cocinas",
    excerpt:
      "Durante el servicio nadie puede coger el teléfono. Auria sí. Recibe la reserva, confirma disponibilidad en tu Google Calendar y envía un recordatorio automático al cliente.",
    img: "https://readdy.ai/api/search-image?query=elegant%20restaurant%20interior%20with%20warm%20candlelight%2C%20empty%20tables%20set%20for%20dinner%20service%2C%20amber%20tones%2C%20moody%20sophisticated%20atmosphere%2C%20fine%20dining%20ambiance%2C%20no%20people%2C%20professional%20interior%20photography&width=800&height=500&seq=auria_blog_03&orientation=landscape",
    body: [
      {
        type: "paragraph",
        content: "El momento del servicio en un restaurante es sagrado. La cocina vuela, el personal de sala está con los clientes, y el teléfono... suena. Y suena. Y suena. Nadie lo coge. El cliente que quería reservar mesa para su aniversario el sábado ya está llamando al restaurante de enfrente."
      },
      {
        type: "heading",
        content: "El teléfono: el gran enemigo durante el servicio"
      },
      {
        type: "paragraph",
        content: "Los datos son contundentes: la mayoría de restaurantes pierde entre el 30% y el 50% de las llamadas entrantes durante el servicio de mediodía y noche. No por falta de ganas, sino por imposibilidad física. Y el 80% de esas personas no vuelve a llamar."
      },
      {
        type: "heading",
        content: "Qué hace Auria por un restaurante"
      },
      {
        type: "list",
        content: [
          "Atiende las llamadas en tiempo real durante todo el servicio, sin interrupciones.",
          "Consulta disponibilidad en el sistema de reservas y confirma en el momento.",
          "Recoge preferencias especiales: alergias, celebraciones, menú infantil.",
          "Envía confirmación por SMS al cliente y actualiza el calendario automáticamente.",
          "Gestiona cancelaciones y modifica reservas sin necesidad de humano."
        ]
      },
      {
        type: "heading",
        content: "Configuración en menos de una tarde"
      },
      {
        type: "paragraph",
        content: "Solo necesitas conectar tu Google Calendar o sistema de reservas, definir la capacidad del local y el horario, y activar Auria. En menos de 30 minutos el agente ya conoce tu carta, tus precios medios, si tienes terraza, dónde estáis, cómo se llega y cualquier detalle que quieras compartir. A partir de ahí, trabaja solo."
      }
    ]
  },
  {
    slug: "31-idiomas-auria",
    category: "Producto",
    date: "1 mar 2026",
    readTime: "4 min",
    featured: false,
    title: "31 idiomas, 1 recepcionista: cómo Auria atiende a clientes internacionales sin esfuerzo",
    excerpt:
      "Auria detecta automáticamente el idioma del llamante y responde en su lengua nativa. Ideal para zonas turísticas, clínicas con pacientes extranjeros y empresas con clientes globales.",
    img: "https://readdy.ai/api/search-image?query=world%20map%20with%20soft%20glowing%20connection%20lines%20between%20countries%2C%20warm%20amber%20and%20cream%20color%20palette%2C%20minimalist%20flat%20style%20illustration%2C%20global%20communication%20concept%2C%20clean%20white%20background%2C%20modern%20infographic%20design&width=800&height=500&seq=auria_blog_04&orientation=landscape",
    body: [
      {
        type: "paragraph",
        content: "Para un negocio en una zona turística o con clientes internacionales, el idioma siempre ha sido una barrera. Contratar personal multilingüe es caro y difícil. Usar traductores en tiempo real es torpe e incómodo. Y pedir a un cliente que te escriba porque no le entiendes es, simplemente, perderle."
      },
      {
        type: "heading",
        content: "Detección automática de idioma en tiempo real"
      },
      {
        type: "paragraph",
        content: "Auria identifica el idioma del llamante en los primeros segundos de conversación y cambia automáticamente a ese idioma para toda la llamada. No hace falta que el cliente elija idioma en ningún menú. No hace falta configurar nada especial. Simplemente habla, y Auria le responde en su lengua."
      },
      {
        type: "list",
        content: [
          "Español, inglés, francés, alemán, italiano, portugués",
          "Árabe, chino mandarín, japonés, coreano, ruso",
          "Neerlandés, polaco, sueco, noruego, danés, finlandés",
          "Catalán, euskera, gallego y 12 idiomas más"
        ]
      },
      {
        type: "heading",
        content: "Casos de uso reales"
      },
      {
        type: "paragraph",
        content: "Clínicas en zonas costeras que atienden turistas europeos, hoteles boutique con clientes de todo el mundo, despachos de abogados con clientes extranjeros o empresas SaaS con usuarios en múltiples países. En todos estos casos, Auria actúa como un recepcionista nativo en cada idioma, con el mismo nivel de calidad y detalle."
      },
      {
        type: "paragraph",
        content: "La tecnología de voz de Auria está construida sobre los modelos de lenguaje más avanzados disponibles, con síntesis de voz natural que elimina el acento robótico característico de los asistentes de voz tradicionales. Tus clientes no sabrán que están hablando con IA — a menos que tú quieras decírselo."
      }
    ]
  },
  {
    slug: "centros-estetica-auria",
    category: "Casos de uso",
    date: "22 feb 2026",
    readTime: "3 min",
    featured: false,
    title: "Centros de estética y peluquerías: el agente de Auria que nunca olvida una cita",
    excerpt:
      "En un salón, las manos siempre están ocupadas. Auria gestiona la agenda de todos los profesionales, recuerda citas a los clientes y acepta cancelaciones con nota automática.",
    img: "https://readdy.ai/api/search-image?query=modern%20luxury%20beauty%20salon%20interior%20with%20clean%20white%20and%20rose%20gold%20decor%2C%20professional%20styling%20stations%2C%20elegant%20minimalist%20design%2C%20warm%20soft%20lighting%2C%20no%20people%2C%20high-end%20interior%20photography&width=800&height=500&seq=auria_blog_05&orientation=landscape",
    body: [
      {
        type: "paragraph",
        content: "En un centro de estética o peluquería, las manos están ocupadas —literal y constantemente. Cortar, teñir, depilación láser, manicura, masajes... Coger el teléfono en medio de un servicio no es solo incómodo: puede arruinar el resultado. Y sin embargo, el teléfono es el canal principal por el que llegan las reservas."
      },
      {
        type: "heading",
        content: "El desafío de gestionar múltiples agendas"
      },
      {
        type: "paragraph",
        content: "Un salón con tres o más profesionales tiene tres agendas distintas, con servicios de duración variable, pausas, preferencias de horario y días de descanso diferentes. Coordinar todo eso por teléfono, en tiempo real, mientras atiendes a alguien, es casi imposible sin cometer errores. Las dobles reservas y los huecos vacíos son el pan de cada día."
      },
      {
        type: "heading",
        content: "Lo que Auria hace en un salón de belleza"
      },
      {
        type: "list",
        content: [
          "Conoce los servicios de cada profesional, sus tiempos y sus precios.",
          "Gestiona la agenda de todo el equipo en tiempo real sin solapamientos.",
          "Envía recordatorio automático 24h antes para reducir no-shows.",
          "Acepta cancelaciones y rellena el hueco ofreciendo el espacio a otros clientes en lista de espera.",
          "Recoge preferencias del cliente: profesional favorito, tipo de servicio, historial."
        ]
      },
      {
        type: "paragraph",
        content: "El impacto más inmediato que reportan los salones es la reducción de no-shows, que baja entre un 40% y un 60% gracias a los recordatorios automáticos. El segundo impacto es la ocupación: al gestionar listas de espera automáticamente, los huecos que antes quedaban vacíos ahora se rellenan solos."
      }
    ]
  },
  {
    slug: "seguridad-datos-auria",
    category: "Producto",
    date: "15 feb 2026",
    readTime: "6 min",
    featured: false,
    title: "Privacidad y RGPD: cómo Auria protege los datos de tus llamadas desde el primer segundo",
    excerpt:
      "PII Redaction automático, cifrado AES-256, servidores en la UE y arquitectura Zero-Knowledge. Todo lo que necesitas saber sobre cómo Auria gestiona la privacidad de tus clientes.",
    img: "https://readdy.ai/api/search-image?query=abstract%20digital%20security%20concept%20with%20glowing%20shield%20and%20lock%20icon%2C%20dark%20background%20with%20subtle%20amber%20and%20white%20light%20particles%2C%20minimalist%20tech%20art%2C%20data%20protection%20visualization%2C%20clean%20composition&width=800&height=500&seq=auria_blog_06&orientation=landscape",
    body: [
      {
        type: "paragraph",
        content: "Cuando un paciente llama a una clínica, un cliente llama a su banco o un usuario contacta con cualquier servicio, está compartiendo información personal sensible. Nombre, fecha de nacimiento, número de teléfono, dirección, detalles médicos... Toda esa información pasa a través de Auria. Y protegerla no es opcional: es una obligación legal y ética."
      },
      {
        type: "heading",
        content: "PII Redaction: los datos sensibles nunca se almacenan en bruto"
      },
      {
        type: "paragraph",
        content: "La primera capa de protección de Auria es el PII Redaction automático. Durante el procesamiento de cada llamada, el sistema detecta y elimina o anonimiza cualquier dato personal identificable (Personally Identifiable Information) antes de que se almacene en los logs. Números de DNI, teléfonos, direcciones, datos de tarjetas... nada de eso queda expuesto en texto plano."
      },
      {
        type: "heading",
        content: "Cifrado AES-256 en reposo y en tránsito"
      },
      {
        type: "paragraph",
        content: "Todos los datos de Auria —grabaciones, transcripciones, metadatos de llamadas— están cifrados con AES-256, el estándar utilizado por bancos y gobiernos. Esto aplica tanto a los datos en tránsito (mientras viajan por la red) como a los datos en reposo (almacenados en servidores). Sin la clave de descifrado, los datos son ilegibles."
      },
      {
        type: "heading",
        content: "Infraestructura 100% en la Unión Europea"
      },
      {
        type: "paragraph",
        content: "Todos los servidores de Auria están ubicados en centros de datos dentro de la Unión Europea, garantizando que los datos de tus clientes no salen en ningún momento del territorio europeo. Esto es fundamental para el cumplimiento del RGPD, que exige que los datos personales de ciudadanos europeos se procesen y almacenen bajo jurisdicción europea."
      },
      {
        type: "heading",
        content: "Arquitectura Zero-Knowledge"
      },
      {
        type: "list",
        content: [
          "Auria no puede acceder al contenido de las llamadas de tus clientes sin tu autorización explícita.",
          "Cada cuenta tiene su propio entorno aislado. Ningún dato cruza entre clientes.",
          "Los accesos al sistema quedan registrados en un log de auditoría inmutable.",
          "Cumplimiento certificado con RGPD, ISO 27001 y ENS (Esquema Nacional de Seguridad)."
        ]
      },
      {
        type: "paragraph",
        content: "La privacidad no es una característica añadida a posteriori en Auria: es parte del diseño desde el primer día. Si tu negocio maneja datos sensibles —sanitario, legal, financiero— y necesitas documentación técnica sobre seguridad para procesos de auditoría o compliance, puedes solicitarla directamente a nuestro equipo en auriareceptionistai@gmail.com."
      }
    ]
  }
];
